import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Building2, Zap, RefreshCw, CheckCircle, AlertTriangle, ArrowLeft, Save, Mail } from 'lucide-react';

// Etapas del proceso (stepper en vivo)
const ETAPAS = [
  { key: 'SUCURSAL', label: 'Sucursal' },
  { key: 'USUARIO', label: 'Usuario' },
  { key: 'MP', label: 'Medios de Pago' },
];

const etapaIndex = (prog) => {
  if (!prog) return -1;
  if (prog.etapa === 'COMPLETADO' || prog.completado) return 3;
  const t = (prog.paso || '').toLowerCase();
  if (t.includes('complet')) return 3;
  if (/(medio|qr|saldo|billetera|mediopago)/.test(t)) return 2;
  if (/(asign|usuario)/.test(t)) return 1;
  return 0; // sucursal / iniciando
};

export default function CrearSucursal() {
  const navigate = useNavigate();
  const [empresas, setEmpresas] = useState([]);
  const [rubros, setRubros] = useState([]);
  const [email, setEmail] = useState('');
  const [empresaId, setEmpresaId] = useState('');
  const [rubroId, setRubroId] = useState('');
  const [nombreSucursal, setNombreSucursal] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [refreshingRubros, setRefreshingRubros] = useState(false);
  const [ejecutando, setEjecutando] = useState(false);
  const [progreso, setProgreso] = useState(null);
  const [resultadoFinal, setResultadoFinal] = useState(null); // 'OK' | 'ERROR'

  useEffect(() => {
    (async () => {
      try {
        const [empRes, rubRes, defRes] = await Promise.all([
          api.get('/empresas', { params: { limit: 100 } }),
          api.get('/waya/rubros'),
          api.get('/cuentas-juridicas/config/sucursal-defaults'),
        ]);
        const linked = (Array.isArray(empRes.data) ? empRes.data : []).filter(e => e.waya_empresa_id);
        setEmpresas(linked);
        setRubros(rubRes.data?.rubros || []);
        const def = defRes.data || {};
        if (def.default_empresa_id && linked.some(e => e.id === def.default_empresa_id)) setEmpresaId(def.default_empresa_id);
        if (def.default_rubro_id) setRubroId(def.default_rubro_id);
      } catch {
        toast.error('Error al cargar empresas/rubros');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleRefreshRubros = async () => {
    setRefreshingRubros(true);
    try {
      const res = await api.post('/waya/rubros/refresh');
      setRubros(res.data?.rubros || []);
      toast.success(`Rubros actualizados desde Waya (${res.data?.count || 0})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al actualizar rubros');
    } finally {
      setRefreshingRubros(false);
    }
  };

  const handleGuardarDefaults = async () => {
    setSavingDefaults(true);
    try {
      await api.put('/cuentas-juridicas/config/sucursal-defaults', {
        default_empresa_id: empresaId || null,
        default_rubro_id: rubroId || null,
      });
      toast.success('Empresa y comisión guardadas como predeterminadas');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar predeterminados');
    } finally {
      setSavingDefaults(false);
    }
  };

  const handleCrear = async () => {
    if (!email.trim()) { toast.error('Ingresá el email de la cuenta'); return; }
    if (!empresaId) { toast.error('Elegí la empresa'); return; }
    if (!rubroId) { toast.error('Elegí la comisión (rubro)'); return; }
    setEjecutando(true);
    setResultadoFinal(null);
    setProgreso({ paso: 'Iniciando...', etapa: 'SUCURSAL', completado: false });
    const mail = email.trim();
    try {
      await api.post('/cuentas-juridicas/continuar', {
        empresa_id: empresaId,
        email: mail,
        aprobar_identidad: false, // la cuenta ya está activa; solo creamos sucursal + usuario + MP
        skip_activacion: true, // herramienta de creación: NO re-activa, va directo a crear la sucursal
        crear_sucursal: true,
        forzar_sucursal: true, // herramienta de creación: siempre crea (no saltea por id viejo en la base)
        nombre_sucursal: nombreSucursal.trim() || null,
        rubro_id: rubroId,
      }, { skipAuthRedirect: true });
      toast.info('Proceso iniciado. Creando sucursal en segundo plano...');
      // Polling del progreso por email
      let elapsed = 0;
      let sawProgress = false;
      const iv = setInterval(async () => {
        elapsed += 3500;
        try {
          const r = await api.get('/cuentas-juridicas/progreso-por-email', {
            params: { empresa_id: empresaId, email: mail }, skipAuthRedirect: true,
          });
          if (r.data?.progreso) {
            sawProgress = true;
            setProgreso(r.data.progreso);
            if (r.data.progreso.completado) {
              clearInterval(iv);
              const errs = r.data.progreso.paso?.toLowerCase().includes('error');
              setResultadoFinal(errs ? 'ERROR' : 'OK');
              setEjecutando(false);
              if (errs) toast.error('El proceso terminó con error');
              else toast.success('Sucursal creada correctamente');
            }
          }
        } catch { /* seguir */ }
        // Si tras 45s no hay progreso, probablemente el email no se encontró en Waya
        if (!sawProgress && elapsed >= 45000) {
          clearInterval(iv);
          setEjecutando(false);
          setResultadoFinal('ERROR');
          setProgreso({ paso: 'No se encontró la cuenta en Waya para ese email (verificá que exista y esté activa).', completado: true });
          toast.error('No se encontró la cuenta en Waya para ese email');
        }
        if (elapsed > 480000) { clearInterval(iv); setEjecutando(false); }
      }, 3500);
    } catch (e) {
      setEjecutando(false);
      setResultadoFinal('ERROR');
      toast.error(e.response?.data?.detail || 'Error al iniciar el proceso');
    }
  };

  const curIdx = etapaIndex(progreso);
  const isErr = resultadoFinal === 'ERROR' || progreso?.paso?.toLowerCase().includes('error');

  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="crear-sucursal-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4" data-testid="crear-sucursal-back">
        <ArrowLeft size={16} /> Volver
      </button>

      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-xl bg-teal-600 flex items-center justify-center">
          <Building2 className="text-white" size={20} />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Crear Sucursal</h1>
          <p className="text-sm text-slate-500">Ingresá un email de una cuenta ya activa en Waya → crea la sucursal, asigna el usuario y configura los medios de pago.</p>
        </div>
      </div>

      <Card className="p-6 mt-6 space-y-4 bg-white">
        <div>
          <Label className="text-sm flex items-center gap-1.5"><Mail size={14} /> Email de la cuenta (usuario Waya)</Label>
          <Input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="cuenta@ejemplo.com"
            disabled={ejecutando}
            className="mt-1"
            data-testid="crear-sucursal-email"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm">Empresa (Waya)</Label>
            <select
              value={empresaId}
              onChange={(e) => setEmpresaId(e.target.value)}
              disabled={ejecutando || loading}
              className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
              data-testid="crear-sucursal-empresa"
            >
              <option value="">— Elegí empresa —</option>
              {empresas.map(e => (
                <option key={e.id} value={e.id}>{e.razon_social} (#{e.waya_empresa_id})</option>
              ))}
            </select>
            {!loading && empresas.length === 0 && (
              <p className="text-[11px] text-amber-600 mt-1">No hay empresas vinculadas a Waya todavía.</p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="text-sm">Comisión (Rubro)</Label>
              <button
                onClick={handleRefreshRubros}
                disabled={refreshingRubros || ejecutando}
                className="text-[11px] text-teal-600 hover:text-teal-800 flex items-center gap-1 disabled:opacity-50"
                data-testid="crear-sucursal-refresh-rubros"
              >
                <RefreshCw size={11} className={refreshingRubros ? 'animate-spin' : ''} /> Actualizar rubros
              </button>
            </div>
            <select
              value={rubroId}
              onChange={(e) => setRubroId(e.target.value)}
              disabled={ejecutando || loading}
              className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
              data-testid="crear-sucursal-rubro"
            >
              <option value="">— Elegí comisión —</option>
              {rubros.map(r => (
                <option key={r.id} value={r.id}>{r.nombre}{r.porcentaje != null ? ` — ${r.porcentaje}%` : ''}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <Label className="text-sm">Nombre de la sucursal (opcional)</Label>
          <Input
            value={nombreSucursal}
            onChange={(e) => setNombreSucursal(e.target.value)}
            placeholder="Por defecto: razón social de la cuenta"
            disabled={ejecutando}
            className="mt-1"
            data-testid="crear-sucursal-nombre"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-2">
          <Button onClick={handleCrear} disabled={ejecutando} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="crear-sucursal-submit">
            {ejecutando ? <><RefreshCw size={16} className="animate-spin mr-2" /> Creando...</> : <><Zap size={16} className="mr-2" /> Crear sucursal</>}
          </Button>
          <Button variant="outline" onClick={handleGuardarDefaults} disabled={savingDefaults || ejecutando} data-testid="crear-sucursal-save-defaults">
            <Save size={15} className="mr-2" /> {savingDefaults ? 'Guardando...' : 'Guardar empresa + comisión por defecto'}
          </Button>
        </div>
      </Card>

      {/* Stepper en vivo */}
      {progreso && (
        <Card className="p-5 mt-4" style={{ backgroundColor: resultadoFinal ? (isErr ? '#fef2f2' : '#f0fdf4') : '#eff6ff' }} data-testid="crear-sucursal-progreso">
          <div className="flex items-center mb-3">
            {ETAPAS.map((et, i) => {
              const done = curIdx > i || (resultadoFinal === 'OK');
              const active = !resultadoFinal && curIdx === i;
              const dotColor = done ? '#22c55e' : active ? '#3b82f6' : '#cbd5e1';
              return (
                <React.Fragment key={et.key}>
                  <div className="flex flex-col items-center" style={{ minWidth: 70 }} data-testid={`crear-etapa-${et.key}-${done ? 'done' : active ? 'active' : 'pending'}`}>
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: dotColor }}>
                      {done ? <CheckCircle size={15} /> : active ? <RefreshCw size={13} className="animate-spin" /> : <span className="text-xs">{i + 1}</span>}
                    </div>
                    <span className="text-[10px] mt-1 text-center" style={{ color: active ? '#2563eb' : done ? '#16a34a' : '#94a3b8' }}>{et.label}</span>
                  </div>
                  {i < ETAPAS.length - 1 && (
                    <div className="flex-1 h-0.5 mb-4" style={{ backgroundColor: (curIdx > i || resultadoFinal === 'OK') ? '#22c55e' : '#e2e8f0' }} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            {!resultadoFinal && <RefreshCw size={14} className="animate-spin text-blue-600" />}
            {resultadoFinal === 'OK' && <CheckCircle size={14} className="text-green-600" />}
            {isErr && <AlertTriangle size={14} className="text-red-600" />}
            <span className="text-sm font-medium" style={{ color: resultadoFinal ? (isErr ? '#dc2626' : '#16a34a') : '#2563eb' }}>{progreso.paso}</span>
          </div>
        </Card>
      )}
    </div>
  );
}
